import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { catchError, EMPTY, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { NotificationsService } from 'app/services/notifications/notifications.service';
import { NotificationInterface } from 'app/services/notifications/notifications.interface';
import { ConfirmationActionComponent } from 'app/components/ui/dialogs/confirmation-action/confirmation-action.component';
import { ViewComponent as MinuteViewComponent } from 'app/modules/admin/secretary/documents/list/tabs/minutes/dialogs/view/view.component';

const POLL_INTERVAL_MS = 30_000;

@Component({
    selector: 'notification-bell',
    templateUrl: './notification-bell.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
    ],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-8px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-8px)' }))
            ])
        ]),
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ])
        ]),
        trigger('slideOutRight', [
            transition(':leave', [
                style({ opacity: 1, transform: 'translateX(0)' }),
                animate('300ms ease-in', style({ opacity: 0, transform: 'translateX(100%)' }))
            ])
        ])
    ],
})
export class NotificationBellComponent implements OnInit, OnDestroy {

    unreadCount: number = 0;
    showDropdown: boolean = false;
    notifications: NotificationInterface[] = [];
    loading: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _stopPolling: Subject<void> = new Subject<void>();

    constructor(
        private _notificationsService: NotificationsService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _elementRef: ElementRef,
        private _matDialog: MatDialog,
        private _location: Location,
        private _router: Router,
    ) {
    }

    ngOnInit(): void {
        this._startPolling();
    }

    ngOnDestroy(): void {
        this._stopPolling.next();
        this._stopPolling.complete();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Polling do contador de não-lidas — padrão canônico do projeto
     * (processing-progress.component.ts): `timer` + `switchMap` +
     * `catchError(() => EMPTY)` + dois Subjects takeUntil. O `catchError`
     * impede que o 1º erro de rede mate o stream permanentemente
     * (bug KI-NAV-NOTIF-001 — antigo `interval` + `clearInterval` em Subscription).
     */
    private _startPolling(): void {
        this._stopPolling.next();

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this._notificationsService.getUnreadCount()),
                catchError(() => EMPTY),
                takeUntil(this._stopPolling),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe({
                next: (response) => {
                    this.unreadCount = response.count;
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this._elementRef.nativeElement.contains(event.target)) {
            this.showDropdown = false;
        }
    }

    toggleDropdown(): void {
        this.showDropdown = !this.showDropdown;
        if (this.showDropdown) {
            this._loadNotifications();
        }
        this._changeDetectorRef.markForCheck();
    }

    /** Botão "Ver todas" — fecha o dropdown e abre a tela dedicada de notificações. */
    openAllNotifications(): void {
        this.showDropdown = false;
        this._router.navigate(['/notifications']);
        this._changeDetectorRef.markForCheck();
    }

    markAsRead(notification: NotificationInterface): void {
        if (notification.readAt) {
            return;
        }
        this._notificationsService.markAsRead(notification.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    notification.readAt = new Date().toISOString();
                    this.unreadCount = Math.max(0, this.unreadCount - 1);
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    dismissNotification(event: Event, notification: NotificationInterface): void {
        event.stopPropagation();
        this._notificationsService.markAsRead(notification.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    if (this.unreadCount > 0 && !notification.readAt) {
                        this.unreadCount--;
                    }
                    const index = this.notifications.findIndex(n => n.id === notification.id);
                    if (index !== -1) {
                        this.notifications.splice(index, 1);
                    }
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    openNotificationDetail(notification: NotificationInterface): void {
        if (!notification.readAt) {
            this.markAsRead(notification);
        }

        const typeMap: Record<string, string> = {
            success: 'success',
            error: 'error',
            info: 'info',
            warning: 'warning'
        };

        const hasMinuteId = notification.data?.minute_id;
        // Deep-link canônico { route, params } (contrato de notificações
        // [2026-08-29]) — usado pelas contas a pagar (route
        // '/financial/accounts-payable' + params.id) e por futuras
        // notificações genéricas. EXTENSÃO MÍNIMA [2026-09-19].
        //
        // ⚠️ REGRESSÃO CORRIGIDA [2026-09-19, review F-1]: os produtores de ata
        // fazem array_merge do bloco legado (minute_id/minute_title) COM o
        // deep-link canônico (route/params) — TODA notificação de ata chega com
        // `data.route` além de `minute_id`. O fluxo de atas (Location.replaceState
        // + MinuteViewComponent + restauração de URL) deve PREVALECER nesse caso:
        // `hasRoute` exige AUSÊNCIA de minute_id, senão o clique em ata navegaria
        // genericamente em vez do overlay.
        const hasRoute = !!notification.data?.route && !hasMinuteId;

        const yesLabel = hasRoute ? 'Ir para' : (hasMinuteId ? 'Ver ata' : 'OK');

        const dialogRef = this._matDialog.open(ConfirmationActionComponent, {
            width: '400px',
            maxWidth: '90vw',
            data: {
                title: notification.title,
                message: (notification.message || '') + (hasMinuteId ? `\n\nReferência: Ata #${notification.data.minute_id}` : ''),
                type: typeMap[notification.type] || 'info',
                closeable: true,
                yesButton: {
                    use: true,
                    label: yesLabel,
                    color: notification.type === 'error' ? 'red' : undefined
                },
                noButton: { use: false },
                cancelButton: { use: false },
                closeButton: { use: true }
            }
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
            if (result?.action === 'yes') {
                this.showDropdown = false;
                this._changeDetectorRef.markForCheck();

                // Deep-link canônico (data.route + data.params) — navega via
                // router.navigate. Só alcançado quando NÃO há minute_id (o
                // fluxo de atas prevalece — hasRoute já exclui atas).
                if (hasRoute) {
                    this._navigateToRoute(notification);
                    return;
                }

                if (hasMinuteId) {
                    const minuteId = notification.data.minute_id;
                    this._location.replaceState('/secretary/documents/minutes/' + minuteId);

                    const viewRef = this._matDialog.open(MinuteViewComponent, {
                        autoFocus: false,
                        data: { minuteData: { id: minuteId } },
                        disableClose: true,
                        width: '95%',
                        maxWidth: '620px',
                        height: '95%',
                        maxHeight: '750px',
                    });

                    viewRef.afterClosed()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe((viewResult) => {
                        if (viewResult?.action === 'edit') {
                            this._router.navigate(['/secretary/documents/minutes', minuteId], {
                                queryParams: { mode: 'edit' }
                            });
                        } else {
                            this._location.replaceState(this._router.url.split('?')[0].split('/').slice(0, -1).join('/') || '/');
                        }
                    });
                }
            }
        });
    }

    markAllRead(): void {
        this._notificationsService.markAllAsRead()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.notifications.forEach(n => {
                        if (!n.readAt) {
                            n.readAt = new Date().toISOString();
                        }
                    });
                    this.unreadCount = 0;
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    getTimeAgo(dateStr: string): string {
        if (!dateStr) return '';

        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return 'agora';
        if (diffMin < 60) return `${diffMin} min`;
        if (diffHour < 24) return `${diffHour}h`;
        if (diffDay < 7) return `${diffDay}d`;

        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }

    /**
     * Navegação do deep-link canônico { route, params } — EXTENSÃO MÍNIMA e
     * segura do sino [2026-09-19]: `route` (ex.: '/financial/accounts-payable')
     * vira o 1º segmento da navegação; `params.id` é anexado como segmento de
     * path; os demais params seguem como queryParams. Tolera ausência de
     * params (navega só para a rota base). Este método NUNCA é alcançado por
     * notificações de ata (hasRoute exclui datos com minute_id — review F-1).
     */
    private _navigateToRoute(notification: NotificationInterface): void {
        const route = notification.data?.route;
        if (!route) {
            return;
        }

        const params: Record<string, any> = notification.data?.params ?? {};
        const commands: any[] = [route];

        if (params['id'] !== undefined && params['id'] !== null) {
            commands.push(params['id']);
        }

        const queryParams: Record<string, any> = { ...params };
        delete queryParams['id'];

        this._router.navigate(commands, { queryParams });
        this._changeDetectorRef.markForCheck();
    }

    private _loadNotifications(): void {
        this.loading = true;
        this._notificationsService.getNotifications({ page: 1 })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.notifications = response.data;
                    this.loading = false;
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.loading = false;
                    this._changeDetectorRef.markForCheck();
                },
            });
    }
}